import { useEffect, useRef } from 'react';
import { FormRadioSelectGroup } from '~/components/form';
import { FormContextProvider } from '~/hooks/providers/FormContextProvider';
import { Form } from 'semantic-ui-react';
import { useTeamQuery } from '~/requests/team';
import { updateTeamCompany } from '~/api/team';
import { getCompany, patchCompany } from '~/api/company';
import { useForm, useUserContext } from '~/hooks';
import { isEqual } from 'lodash';
import { withPromiseToaster } from '~/utils';

export interface CompanyProjectVisibilityFormState {
  projectVisibility: 'ALL' | 'INVITE_ONLY';
}

type TeamSource = { source: 'team' };

export type CompanyProfileSource = {
  source: 'companyProfile';
  companyId: string;
  projectVisibility: 'ALL' | 'INVITE_ONLY';
  onCompanyUpdated: (company: unknown) => void;
};

export type CompanyProjectVisibilityFormProps = TeamSource | CompanyProfileSource;

function CompanyProjectVisibilityFormInner({ props }: { props: CompanyProjectVisibilityFormProps }) {
  const { formState } = useForm<CompanyProjectVisibilityFormState>();
  const { refetch: refetchTeam } = useTeamQuery();
  const propsRef = useRef(props);
  propsRef.current = props;

  const formStateRef = useRef(formState);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (formState.projectVisibility && !isEqual(formState, formStateRef.current)) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        const p = propsRef.current;
        if (p.source === 'team') {
          void withPromiseToaster(
            updateTeamCompany(formState)
              .then(() => refetchTeam())
              .catch((err: unknown) => {
                console.error(err);
                throw err;
              }),
            { messageStub: 'updating global company settings' }
          );
        } else {
          void withPromiseToaster(
            patchCompany(p.companyId, { projectVisibility: formState.projectVisibility })
              .then(() => getCompany(p.companyId))
              .then((c) => p.onCompanyUpdated(c))
              .catch((err: unknown) => {
                console.error(err);
                throw err;
              }),
            { messageStub: 'updating global company settings' }
          );
        }
      }, 3000);
      formStateRef.current = formState;
    }
  }, [formState, refetchTeam]);

  return (
    <Form>
      <FormRadioSelectGroup
        schemaKey="projectVisibility"
        fieldLabel="Global company settings"
        fieldStyle={{
          marginRight: '6rem',
        }}
        helpText="Standard users should be able to see..."
        inline
        values={[
          { value: 'ALL', label: 'All projects', disabled: false },
          { value: 'INVITE_ONLY', label: 'Only projects to which they have been invited', disabled: false },
        ]}
      />
    </Form>
  );
}

/**
 * Project visibility (global company settings). Used on /settings/company (team) and company profile Settings tab (companyProfile).
 */
export function CompanyProjectVisibilityForm(props: CompanyProjectVisibilityFormProps) {
  const { data: team, status: teamStatus } = useTeamQuery();
  const { loggedInUser } = useUserContext();

  if (props.source === 'team') {
    if (loggedInUser.isAdmin) {
      return null;
    }
    if (teamStatus !== 'success' || !team) {
      return null;
    }
  }

  const defaults: CompanyProjectVisibilityFormState =
    props.source === 'team'
      ? { projectVisibility: team.projectVisibility ?? 'ALL' }
      : { projectVisibility: props.projectVisibility };

  const providerKey =
    props.source === 'companyProfile'
      ? `${props.companyId}-${props.projectVisibility}`
      : `team-${team!.projectVisibility}`;

  return (
    <FormContextProvider key={providerKey} defaults={defaults}>
      <CompanyProjectVisibilityFormInner props={props} />
    </FormContextProvider>
  );
}

export default CompanyProjectVisibilityForm;
